/**
 * Pipeline & Facility batch 6 — the AI drafts (fieldiq-api#1236). Hand-typed
 * from routes/pipeline-facility-ai.routes.ts and lib/pf-ai-digests.ts; the
 * manifest mirror is re-copied after that PR merges.
 *
 * camelCase reads, `{ success, data }` with no meta. Every run is a HITL draft
 * (agent_runs, outcome 'pending'): `data` is the API's deterministic result,
 * `draft` the model's text (null when it was skipped or rejected — `aiError`
 * says why). Nothing is published until a person approves it.
 */

export const PF_AI_AGENTS = ['pf_daily_summary', 'pf_ticket_preflight', 'pf_compliance_digest'] as const;
export type PfAiAgent = (typeof PF_AI_AGENTS)[number];

export interface PfAiDraft {
  narrative: string;
  observations: string[];
}

// ─── Daily summary ───────────────────────────────────────────────────────────

export interface PfDailySummaryData {
  job: { id: string; jobNumber: string; customer: string; site: string; status: string };
  workDate: string;
  dailyLogId: string;
  weather: string | null;
  crew: Array<{ userId: string; name: string; role: string | null; hours: number | null; standbyHours: number | null }>;
  crewCount: number | null;
  crewHours: number | null;
  crewStandbyHours: number | null;
  units: Array<{ equipmentId: string; label: string; hours: number | null; standbyHours: number | null }>;
  unitHours: number | null;
  rentIns: Array<{ rentInId: string; label: string; vendor: string | null; billable: boolean; hours: number | null; standbyHours: number | null }>;
  progress: { unit: 'm' | 'ft'; from: number; to: number; length: number } | null;
  digs: Array<{ digNumber: string; event: 'excavated' | 'backfilled' | string }>;
  narrative: string | null;
  safetyNotes: string | null;
}

// ─── Preflight (one ticket, or one job's open tickets) ───────────────────────

export interface PfPreflightRateGap {
  id: string;
  label: string;
  days: string[];
  reason: string | null;
}

export interface PfPreflightData {
  scope: 'ticket' | 'job';
  job: { id: string; jobNumber: string };
  ticket: { id: string; ticketNumber: string; status: string; scope: 'job' | 'day'; workDate: string | null } | null;
  currency: string | null;
  ticketsChecked: number | null;
  unpricedLines: Array<{ ticketId: string; ticketNumber: string; lineId: string; description: string | null; reason: string | null }>;
  logsWithoutTicket: Array<{ dailyLogId: string; workDate: string }>;
  crewWithoutRate: PfPreflightRateGap[];
  unitsWithoutRate: PfPreflightRateGap[];
  rentInsWithoutRate: PfPreflightRateGap[];
  flagCount: number | null;
  ready: boolean | null;
}

// ─── Compliance digest ───────────────────────────────────────────────────────

export type PfComplianceState = 'expired' | 'missing' | 'due_soon' | 'day_limit_reached';

export interface PfComplianceItemBase {
  state: PfComplianceState | string;
  dueOn: string | null;
}

export interface PfComplianceDigestData {
  today: string;
  oneCall: Array<PfComplianceItemBase & { digNumber: string; jobNumber: string; ticket: string | null }>;
  crewCerts: Array<PfComplianceItemBase & { userId: string; name: string; cert: string; jobNumbers: string[] }>;
  equipment: Array<PfComplianceItemBase & { equipmentId: string; label: string; jobNumbers: string[]; daysUsed: number | null; dayLimit: number | null }>;
  skipped: Array<{ check: string; reason: string }>;
  total: number | null;
}

// ─── A run, as POST returns it and as the register stores it ─────────────────

/** POST /ai/daily-summary | /ai/ticket-preflight/:id | /ai/job-preflight/:id | /ai/compliance-digest — 201. */
export interface PfAiRunResult<T = unknown> {
  agentRunId: string;
  agent: PfAiAgent | string;
  status: 'draft' | string;
  data: T | null;
  draft: PfAiDraft | null;
  aiError: string | null;
}

/** GET /ai/digests (≤ 20, newest first) and GET /ai/digests/:id — shapePfRun. */
export interface PfAiStoredRun<T = unknown> {
  agentRunId: string;
  agent: PfAiAgent | string;
  status: string;
  /** agent_runs.outcome: pending | actioned | dismissed | superseded | no_action. */
  outcome: string | null;
  trigger: string | null;
  triggerData: Record<string, unknown> | null;
  data: T | null;
  draft: PfAiDraft | null;
  /** The text a person approved (their edit, or the draft as it was). */
  approved: PfAiDraft | null;
  approvedEdited: boolean | null;
  aiError: string | null;
  createdAt: string | null;
  reviewedAt: string | null;
}

/** POST /ai/digests/:id/approve — omit both to approve the draft as written. */
export interface PfAiApproveBody {
  narrative?: string;
  observations?: string[];
}
